import { useState, useCallback, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import type { Toast, ToastType } from '../types';
import type { SanitizedError } from '../utils/errorSanitizer';

interface UseToastReturn {
  toasts: Toast[];
  showToast: (type: ToastType, title: string, message: string, duration?: number) => void;
  /**
   * Show an error toast that renders via i18n from a SanitizedError.
   * Use this instead of showToast('error', ...) whenever you have a JobError
   * with a `sanitized` field — it guarantees no raw backend strings leak to the UI.
   */
  showSanitizedErrorToast: (title: string, sanitized: SanitizedError, duration?: number) => void;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
}

export const useToast = (): UseToastReturn => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timerMapRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Cleanup all timers on unmount
  useEffect(() => {
    const timerMap = timerMapRef.current;
    return () => {
      timerMap.forEach((timer) => clearTimeout(timer));
      timerMap.clear();
    };
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    const timer = timerMapRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timerMapRef.current.delete(id);
    }
  }, []);

  const _scheduleRemoval = useCallback(
    (id: string, duration: number) => {
      if (duration > 0) {
        const timer = setTimeout(() => {
          removeToast(id);
        }, duration);
        timerMapRef.current.set(id, timer);
      }
    },
    [removeToast]
  );

  const showToast = useCallback(
    (type: ToastType, title: string, message: string, duration: number = 5000) => {
      // Toast.tsx forces the friendly i18n message when type='error', so the
      // raw `message` would otherwise be discarded. Capture it to Sentry so the
      // dev team can still investigate. Title carries the page context.
      if (type === 'error' && message) {
        Sentry.captureMessage(`Client-side error toast: ${title}`, {
          level: 'warning',
          extra: { raw_message: message, title },
        });
      }
      const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
      const newToast: Toast = { id, type, title, message, duration };
      setToasts((prev) => [...prev, newToast]);
      _scheduleRemoval(id, duration);
    },
    [_scheduleRemoval]
  );

  const showSanitizedErrorToast = useCallback(
    (title: string, sanitized: SanitizedError, duration: number = 7000) => {
      const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
      // message is intentionally empty — Toast.tsx renders from sanitized.i18nKey
      const newToast: Toast = {
        id,
        type: 'error',
        title,
        message: '',
        sanitized,
        duration,
      };
      setToasts((prev) => [...prev, newToast]);
      _scheduleRemoval(id, duration);
    },
    [_scheduleRemoval]
  );

  const clearAllToasts = useCallback(() => {
    timerMapRef.current.forEach((timer) => clearTimeout(timer));
    timerMapRef.current.clear();
    setToasts([]);
  }, []);

  return {
    toasts,
    showToast,
    showSanitizedErrorToast,
    removeToast,
    clearAllToasts,
  };
};
